import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle2, XCircle, MessageCircleQuestion, Edit, Send, ClipboardCheck,
  UserCheck, PlayCircle, FileText, Clock, ShieldAlert, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import WorkflowBadge from "@/pages/onboarding/WorkflowBadge";
import { STATUS_LABELS, canCreateRequest, canMedicalReview, canOperationalReview, canAssignTeam, canConductVisit } from "@/pages/onboarding/constants";
import AttachmentsPanel from "@/pages/onboarding/AttachmentsPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Helper: a small field-display
function Field({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      <div className="text-sm text-[#1F2937] mt-0.5">{value || "—"}</div>
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role_code || "viewer";

  const [request, setRequest] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [comments, setComments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [infoReasons, setInfoReasons] = useState([]);
  const [newComment, setNewComment] = useState("");

  // Action modals
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveStage, setApproveStage] = useState("medical"); // medical | operational
  const [decisionNotes, setDecisionNotes] = useState("");

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectStage, setRejectStage] = useState("medical");
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectComments, setRejectComments] = useState("");

  const [infoOpen, setInfoOpen] = useState(false);
  const [requestedInfo, setRequestedInfo] = useState("");
  const [infoComments, setInfoComments] = useState("");

  // Assessment + assignment + checklist sheets
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, c, a] = await Promise.all([
        api.get(`/onboarding/requests/${id}`),
        api.get(`/onboarding/requests/${id}/timeline`),
        api.get(`/onboarding/requests/${id}/comments`),
        api.get(`/onboarding/requests/${id}/audit`),
      ]);
      setRequest(r.data);
      setTimeline(t.data.items || []);
      setComments(c.data.items || []);
      setAudit(a.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    async function loadLookups() {
      try {
        const cats = await api.get("/lookups/categories", { params: { page_size: 200 } });
        const rej = cats.data.items.find((c) => c.code === "rejection_reasons");
        const inf = cats.data.items.find((c) => c.code === "info_request_reasons");
        if (rej) {
          const v = await api.get("/lookups/values", { params: { category_id: rej.id } });
          setRejectionReasons(v.data.items);
        }
        if (inf) {
          const v = await api.get("/lookups/values", { params: { category_id: inf.id } });
          setInfoReasons(v.data.items);
        }
      } catch (_e) {}
    }
    loadLookups();
  }, []);

  if (loading || !request) {
    return <div className="p-6 text-sm text-[#6B7280]" data-testid="ob-detail-loading">Loading…</div>;
  }

  const status = request.status;
  const isCreator = user?.id === request.created_by;
  const canEdit = (status === "draft" || status === "information_requested") && (isCreator || role === "admin");
  const canSubmit = canEdit;
  const canMedDecide = status === "under_medical_review" && canMedicalReview(role);
  const canOpDecide = ["medical_approved", "under_operational_assessment"].includes(status) && canOperationalReview(role);
  const canAssign = status === "operationally_approved" && canAssignTeam(role);
  const canStartVisit = status === "assigned" && canConductVisit(role);
  const canSaveChecklist = ["assigned", "visit_in_progress"].includes(status) && canConductVisit(role);
  const canCompleteVisit = status === "visit_in_progress" && canConductVisit(role);

  // ---------- action handlers ----------
  async function callAction(url, body = null, successMsg = "Done") {
    try {
      const res = body ? await api.post(url, body) : await api.post(url);
      setRequest(res.data);
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function submitApprove() {
    const url = approveStage === "medical"
      ? `/onboarding/requests/${id}/medical-approve`
      : `/onboarding/requests/${id}/operational-approve`;
    if (!decisionNotes.trim()) { toast.error("Decision notes are required."); return; }
    await callAction(url, { decision_notes: decisionNotes }, `${approveStage === "medical" ? "Medical" : "Operational"} approval recorded`);
    setApproveOpen(false); setDecisionNotes("");
  }
  async function submitReject() {
    const url = rejectStage === "medical"
      ? `/onboarding/requests/${id}/medical-reject`
      : `/onboarding/requests/${id}/operational-reject`;
    if (!rejectionReason) { toast.error("Rejection reason is required."); return; }
    if (!rejectComments.trim()) { toast.error("Comments are required."); return; }
    await callAction(url, { rejection_reason: rejectionReason, comments: rejectComments }, "Request rejected");
    setRejectOpen(false); setRejectionReason(""); setRejectComments("");
  }
  async function submitRequestInfo() {
    if (!requestedInfo.trim()) { toast.error("Requested information is required."); return; }
    await callAction(`/onboarding/requests/${id}/request-information`,
      { requested_information: requestedInfo, comments: infoComments }, "Information requested");
    setInfoOpen(false); setRequestedInfo(""); setInfoComments("");
  }

  async function addComment() {
    if (!newComment.trim()) return;
    try {
      await api.post(`/onboarding/requests/${id}/comments`, { body: newComment });
      setNewComment("");
      const c = await api.get(`/onboarding/requests/${id}/comments`);
      setComments(c.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const ribbonActions = (
    <>
      {canEdit && (
        <Button variant="outline" className="border-[#E5E7EB]" onClick={() => navigate(`/onboarding/requests/${id}/edit`)} data-testid="ob-action-edit">
          <Edit className="w-4 h-4 mr-1.5" /> Edit
        </Button>
      )}
      {canSubmit && (
        <Button onClick={() => callAction(`/onboarding/requests/${id}/submit`, {}, "Submitted")} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-action-submit">
          <Send className="w-4 h-4 mr-1.5" /> {status === "information_requested" ? "Resubmit" : "Submit"}
        </Button>
      )}
      {canMedDecide && (
        <>
          <Button variant="outline" className="border-[#F59E0B33] text-[#92400E] hover:bg-[#F59E0B14]" data-testid="ob-action-request-info" onClick={() => setInfoOpen(true)}>
            <MessageCircleQuestion className="w-4 h-4 mr-1.5" /> Request Information
          </Button>
          <Button variant="outline" className="border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412]" data-testid="ob-action-medical-reject" onClick={() => { setRejectStage("medical"); setRejectOpen(true); }}>
            <XCircle className="w-4 h-4 mr-1.5" /> Reject
          </Button>
          <Button className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-action-medical-approve" onClick={() => { setApproveStage("medical"); setApproveOpen(true); }}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Medical Approve
          </Button>
        </>
      )}
      {canOpDecide && (
        <>
          <Button variant="outline" className="border-[#E5E7EB]" data-testid="ob-action-assessment" onClick={() => setAssessmentOpen(true)}>
            <ClipboardCheck className="w-4 h-4 mr-1.5" /> Initial Assessment
          </Button>
          <Button variant="outline" className="border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412]" data-testid="ob-action-operational-reject" onClick={() => { setRejectStage("operational"); setRejectOpen(true); }}>
            <XCircle className="w-4 h-4 mr-1.5" /> Reject
          </Button>
          <Button className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-action-operational-approve" onClick={() => { setApproveStage("operational"); setApproveOpen(true); }}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Operationally Approve
          </Button>
        </>
      )}
      {canAssign && (
        <Button className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-action-assign" onClick={() => setAssignOpen(true)}>
          <UserCheck className="w-4 h-4 mr-1.5" /> Assign Home Care Team
        </Button>
      )}
      {canStartVisit && (
        <Button className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-action-start-visit" onClick={() => callAction(`/onboarding/requests/${id}/start-visit`, null, "Visit started")}>
          <PlayCircle className="w-4 h-4 mr-1.5" /> Start Visit
        </Button>
      )}
      {canSaveChecklist && (
        <Button variant="outline" className="border-[#E5E7EB]" data-testid="ob-action-checklist" onClick={() => setChecklistOpen(true)}>
          <ClipboardCheck className="w-4 h-4 mr-1.5" /> Visit Checklist
        </Button>
      )}
      {canCompleteVisit && (
        <Button className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-action-complete-visit" onClick={() => callAction(`/onboarding/requests/${id}/complete-visit`, null, "Visit completed")}>
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete Visit
        </Button>
      )}
    </>
  );

  return (
    <div data-testid="ob-detail-page">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span>{request.ref_number}</span>
            <WorkflowBadge status={status} testId="ob-detail-status" />
          </span>
        }
        subtitle={`${request.patient?.first_name} ${request.patient?.last_name} · ${request.patient?.mrn} · ${request.clinical?.primary_diagnosis || "—"}`}
        actions={<div className="flex flex-wrap items-center gap-2">{ribbonActions}</div>}
      />

      {request.status === "information_requested" && (
        <div className="mb-4 p-4 rounded-lg bg-[#F59E0B14] border border-[#F59E0B33] text-[#92400E]" data-testid="ob-info-banner">
          <div className="font-semibold mb-1">Information requested by physician</div>
          <div className="text-sm">{request.information_requested}</div>
          {request.information_request_notes && <div className="text-xs mt-1 opacity-80">{request.information_request_notes}</div>}
        </div>
      )}
      {request.status === "rejected" && (
        <div className="mb-4 p-4 rounded-lg bg-[#EF444412] border border-[#EF444433] text-[#B91C1C]" data-testid="ob-rejected-banner">
          <div className="font-semibold mb-1">Request rejected</div>
          <div className="text-sm">Reason: {request.rejection_reason} {request.rejection_notes ? `— ${request.rejection_notes}` : ""}</div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F5F7FA]">
          <TabsTrigger value="overview" data-testid="ob-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="ob-tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="attachments" data-testid="ob-tab-attachments">Attachments</TabsTrigger>
          <TabsTrigger value="comments" data-testid="ob-tab-comments">Comments</TabsTrigger>
          <TabsTrigger value="audit" data-testid="ob-tab-audit">Audit History</TabsTrigger>
          {request.initial_assessment && <TabsTrigger value="assessment" data-testid="ob-tab-assessment">Assessment</TabsTrigger>}
          {request.visit_checklist && <TabsTrigger value="visit" data-testid="ob-tab-visit">Visit Findings</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Patient</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">{request.patient?.first_name} {request.patient?.last_name}</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <Field label="MRN" value={request.patient?.mrn} />
              <Field label="National ID" value={request.patient?.national_id} />
              <Field label="DOB" value={request.patient?.dob} />
              <Field label="Gender" value={request.patient?.gender} />
              <Field label="Nationality" value={request.patient?.nationality} />
              <Field label="Mobile" value={request.patient?.phone} />
              <Field label="Email" value={request.patient?.email} />
              <Field label="City" value={request.patient?.city} />
              <Field label="Address" value={request.patient?.address} full />
            </div>
          </div>
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Referral</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">Referral information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <Field label="Source" value={request.referral?.source} />
              <Field label="Department" value={request.referral?.department} />
              <Field label="Referral date" value={request.referral?.referral_date} />
              <Field label="Referring physician" value={request.referral?.referring_physician} full />
            </div>
          </div>
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Clinical</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">Clinical information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Field label="Primary diagnosis" value={request.clinical?.primary_diagnosis} />
              <Field label="Secondary diagnosis" value={request.clinical?.secondary_diagnosis} />
              <Field label="Allergies" value={request.clinical?.allergies} />
              <Field label="Medications" value={request.clinical?.current_medications} />
              <Field label="Clinical summary" value={request.clinical?.clinical_summary} full />
              <Field label="Clinical notes" value={request.clinical?.clinical_notes} full />
            </div>
          </div>
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Service</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">Service request</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <Field label="Requested service" value={request.service?.requested_service} />
              <Field label="Priority" value={request.service?.priority} />
              <Field label="Expected start" value={request.service?.expected_start_date} />
            </div>
          </div>
          {request.assignment && (
            <div className="hah-card p-5">
              <div className="hah-section-title mb-1">Assignment</div>
              <h3 className="text-base font-semibold text-[#1F2937] mb-4">Home care team</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <Field label="Team" value={request.assignment.team_id} />
                <Field label="Team lead ID" value={request.assignment.team_lead_id} />
                <Field label="Visit date" value={request.assignment.visit_date} />
                <Field label="Visit time" value={request.assignment.visit_time} />
                <Field label="Priority" value={request.assignment.priority} />
                <Field label="Notes" value={request.assignment.notes} full />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <div className="hah-card p-5" data-testid="ob-timeline">
            <ol className="relative border-l border-[#E5E7EB] ml-3 space-y-5">
              {timeline.length === 0 && <li className="ml-4 text-sm text-[#6B7280]">No events yet.</li>}
              {timeline.map((e) => (
                <li key={e.id} className="ml-4 relative">
                  <span className="absolute -left-[24px] top-1 w-3 h-3 rounded-full bg-[#0B7E8C] ring-4 ring-white" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#1F2937]">{e.title}</span>
                    <span className="text-xs text-[#6B7280]">{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                  {e.description && <div className="text-sm text-[#6B7280] mt-1">{e.description}</div>}
                  <div className="text-xs text-[#94A3B8] mt-1">
                    {e.actor_name || e.actor_email || "system"} {e.actor_role ? `· ${e.actor_role.replace(/_/g, " ")}` : ""}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="attachments" className="mt-4">
          <AttachmentsPanel requestId={id} />
        </TabsContent>

        <TabsContent value="comments" className="mt-4">
          <div className="hah-card p-5" data-testid="ob-comments">
            <h3 className="text-base font-semibold text-[#1F2937] mb-3">Comments</h3>
            <div className="space-y-3 mb-4">
              {comments.length === 0 && <div className="text-sm text-[#6B7280]">No comments yet.</div>}
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-3 p-3 rounded-lg border border-[#E5E7EB] bg-[#F5F7FA]/50">
                  <div className="w-8 h-8 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold">
                    {(c.author_email || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#1F2937]">{c.author_name || c.author_email}</span>
                      {c.author_role && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EAF8FA] text-[#0B7E8C]">{c.author_role.replace(/_/g, " ")}</span>}
                      <span className="text-xs text-[#6B7280]">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-[#1F2937] mt-1 whitespace-pre-wrap">{c.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment…"
                rows={2}
                data-testid="ob-comment-input"
                className="flex-1"
              />
              <Button onClick={addComment} disabled={!newComment.trim()} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-comment-submit">
                Post
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="hah-card p-5">
            <h3 className="text-base font-semibold text-[#1F2937] mb-3">Audit history</h3>
            {audit.length === 0 ? (
              <div className="text-sm text-[#6B7280]">No audit events.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F5F7FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-left px-3 py-2">By</th>
                    <th className="text-left px-3 py-2">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-t border-[#E5E7EB]">
                      <td className="px-3 py-2 text-xs text-[#6B7280]">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2"><WorkflowBadge status={a.action.toLowerCase()} /></td>
                      <td className="px-3 py-2 text-[#1F2937]">{a.user_email || "system"}</td>
                      <td className="px-3 py-2 text-xs text-[#6B7280] font-mono">{a.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {request.initial_assessment && (
          <TabsContent value="assessment" className="mt-4">
            <div className="hah-card p-5">
              <h3 className="text-base font-semibold text-[#1F2937] mb-4">Initial assessment</h3>
              {Object.entries({
                "Patient Understanding": request.initial_assessment.patient_understanding,
                "Caregiver": request.initial_assessment.caregiver,
                "Home Environment": request.initial_assessment.home_environment,
                "Accessibility": request.initial_assessment.accessibility,
                "Connectivity": request.initial_assessment.connectivity,
                "Consent Readiness": request.initial_assessment.consent_readiness,
              }).map(([section, body]) => (
                <div key={section} className="mb-4 border border-[#E5E7EB] rounded-lg p-4">
                  <div className="font-medium text-[#1F2937] mb-2">{section}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    {Object.entries((body || {}).answers || {}).map(([k, v]) => (
                      <Field key={k} label={k.replace(/_/g, " ")} value={typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)} />
                    ))}
                  </div>
                  {(body || {}).notes && <div className="text-xs text-[#6B7280] mt-2">{body.notes}</div>}
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        {request.visit_checklist && (
          <TabsContent value="visit" className="mt-4">
            <div className="hah-card p-5">
              <h3 className="text-base font-semibold text-[#1F2937] mb-4">Visit findings</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {Object.entries(request.visit_checklist).filter(([k]) => typeof request.visit_checklist[k] === "boolean").map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    {v ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <XCircle className="w-4 h-4 text-[#EF4444]" />}
                    <span className="capitalize text-[#1F2937]">{k.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
              {request.visit_checklist.additional_findings && <Field label="Additional findings" value={request.visit_checklist.additional_findings} full />}
              {request.visit_checklist.visit_notes && <Field label="Visit notes" value={request.visit_checklist.visit_notes} full />}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* --- Approve dialog --- */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="ob-approve-dialog">
          <DialogHeader>
            <DialogTitle>{approveStage === "medical" ? "Medical approval" : "Operational approval"}</DialogTitle>
            <DialogDescription>
              {approveStage === "medical"
                ? "Approving will forward this request to the H@H Care Coordinator for operational assessment."
                : "Approving will mark this request ready for home care team assignment."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#1F2937]">Decision notes <span className="text-[#EF4444]">*</span></label>
            <Textarea rows={4} value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} data-testid="ob-approve-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={submitApprove} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-approve-confirm">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Reject dialog --- */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="ob-reject-dialog">
          <DialogHeader>
            <DialogTitle>{rejectStage === "medical" ? "Medical rejection" : "Operational rejection"}</DialogTitle>
            <DialogDescription>This will close the workflow. The hospital coordinator will be notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Rejection reason <span className="text-[#EF4444]">*</span></label>
              <Select value={rejectionReason} onValueChange={setRejectionReason}>
                <SelectTrigger data-testid="ob-reject-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {rejectionReasons.map((r) => <SelectItem key={r.id} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Comments <span className="text-[#EF4444]">*</span></label>
              <Textarea rows={4} value={rejectComments} onChange={(e) => setRejectComments(e.target.value)} data-testid="ob-reject-comments" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={submitReject} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="ob-reject-confirm">
              <XCircle className="w-4 h-4 mr-1.5" /> Confirm rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Request information dialog --- */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent data-testid="ob-info-dialog">
          <DialogHeader>
            <DialogTitle>Request information</DialogTitle>
            <DialogDescription>The request will go back to the hospital coordinator for additional details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#1F2937]">What information is needed? <span className="text-[#EF4444]">*</span></label>
              <Textarea rows={3} value={requestedInfo} onChange={(e) => setRequestedInfo(e.target.value)} data-testid="ob-info-requested" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Comments</label>
              <Textarea rows={2} value={infoComments} onChange={(e) => setInfoComments(e.target.value)} data-testid="ob-info-comments" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInfoOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={submitRequestInfo} className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="ob-info-confirm">
              <MessageCircleQuestion className="w-4 h-4 mr-1.5" /> Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Assessment sheet --- */}
      <InitialAssessmentSheet
        open={assessmentOpen}
        onOpenChange={setAssessmentOpen}
        requestId={id}
        initial={request.initial_assessment}
        onSaved={(r) => { setRequest(r); load(); }}
      />

      {/* --- Assign sheet --- */}
      <AssignTeamSheet
        open={assignOpen}
        onOpenChange={setAssignOpen}
        requestId={id}
        onSaved={(r) => { setRequest(r); load(); }}
      />

      {/* --- Visit checklist sheet --- */}
      <VisitChecklistSheet
        open={checklistOpen}
        onOpenChange={setChecklistOpen}
        requestId={id}
        initial={request.visit_checklist}
        canComplete={canCompleteVisit}
        onSaved={(r) => { setRequest(r); load(); }}
      />
    </div>
  );
}


// --------------- Initial Assessment Sheet ---------------
const ASSESSMENT_QUESTIONS = {
  patient_understanding: {
    title: "Patient Understanding",
    items: [
      ["explained", "Program has been explained"],
      ["understands_program", "Patient understands Hospital @ Home"],
      ["willing_to_participate", "Patient willing to participate"],
      ["understands_escalation", "Patient understands escalation process"],
    ],
  },
  caregiver: {
    title: "Caregiver Assessment",
    items: [
      ["available", "Caregiver available"],
      ["willing", "Caregiver willing"],
    ],
    textFields: [
      ["name", "Caregiver name"], ["relationship", "Relationship"], ["contact", "Contact number"],
    ],
  },
  home_environment: {
    title: "Home Environment",
    items: [
      ["safe", "Safe environment"], ["electricity", "Electricity available"], ["water", "Running water available"],
    ],
  },
  accessibility: {
    title: "Accessibility",
    items: [
      ["accessible", "Accessible residence"], ["bedroom", "Suitable bedroom"], ["bathroom", "Suitable bathroom"],
    ],
  },
  connectivity: {
    title: "Connectivity",
    items: [
      ["mobile", "Mobile coverage"], ["internet", "Internet available"], ["smartphone", "Smartphone available"],
    ],
  },
  consent_readiness: {
    title: "Consent Readiness",
    items: [["willing_to_sign", "Patient willing to sign consent"]],
  },
};

function InitialAssessmentSheet({ open, onOpenChange, requestId, initial, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = {};
    Object.keys(ASSESSMENT_QUESTIONS).forEach((sec) => {
      base[sec] = { answers: { ...((initial && initial[sec] && initial[sec].answers) || {}) }, notes: (initial && initial[sec] && initial[sec].notes) || "" };
    });
    setForm(base);
  }, [open, initial]);

  function setAns(sec, key, val) {
    setForm((f) => ({ ...f, [sec]: { ...(f[sec] || {}), answers: { ...((f[sec] || {}).answers || {}), [key]: val } } }));
  }
  function setNotes(sec, val) {
    setForm((f) => ({ ...f, [sec]: { ...(f[sec] || {}), notes: val } }));
  }

  async function save() {
    setSaving(true);
    try {
      const res = await api.post(`/onboarding/requests/${requestId}/initial-assessment`, form);
      onSaved && onSaved(res.data);
      toast.success("Assessment saved");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[700px] overflow-y-auto p-0 bg-[#FAFBFC]">
        <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
          <SheetTitle>Initial Assessment</SheetTitle>
          <SheetDescription>Assess patient suitability before assigning the home care team. Decision is fully manual — no scoring.</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-4">
          {Object.entries(ASSESSMENT_QUESTIONS).map(([sec, def]) => (
            <FormSection key={sec} title={def.title} testId={`ob-assess-${sec}`}>
              {def.items.map(([k, label]) => (
                <FormField key={k} label={label}>
                  <Select
                    value={form[sec]?.answers?.[k] === undefined ? "" : (form[sec].answers[k] ? "yes" : "no")}
                    onValueChange={(v) => setAns(sec, k, v === "yes")}
                  >
                    <SelectTrigger data-testid={`ob-assess-${sec}-${k}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              ))}
              {(def.textFields || []).map(([k, label]) => (
                <FormField key={k} label={label}>
                  <Input
                    value={form[sec]?.answers?.[k] || ""}
                    onChange={(e) => setAns(sec, k, e.target.value)}
                    data-testid={`ob-assess-${sec}-${k}`}
                  />
                </FormField>
              ))}
              <FormField label="Additional notes" full>
                <Textarea
                  rows={2}
                  value={form[sec]?.notes || ""}
                  onChange={(e) => setNotes(sec, e.target.value)}
                  data-testid={`ob-assess-${sec}-notes`}
                />
              </FormField>
            </FormSection>
          ))}
        </div>
        <StickyActionBar>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-assess-save">
            {saving ? "Saving…" : "Save assessment"}
          </Button>
        </StickyActionBar>
      </SheetContent>
    </Sheet>
  );
}


// --------------- Assign Team Sheet ---------------
function AssignTeamSheet({ open, onOpenChange, requestId, onSaved }) {
  const [teams, setTeams] = useState([]);
  const [leads, setLeads] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [form, setForm] = useState({ team_id: "", team_lead_id: "", visit_date: "", visit_time: "", priority: "routine", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/onboarding/teams").then((r) => { setTeams(r.data.teams || []); setLeads(r.data.leads || []); }).catch(() => {});
    api.get("/lookups/categories", { params: { page_size: 200 } }).then(async (cats) => {
      const pri = cats.data.items.find((c) => c.code === "visit_priorities");
      if (pri) {
        const v = await api.get("/lookups/values", { params: { category_id: pri.id } });
        setPriorities(v.data.items);
      }
    }).catch(() => {});
  }, [open]);

  async function save() {
    if (!form.team_id || !form.visit_date) { toast.error("Team and visit date are required."); return; }
    setSaving(true);
    try {
      const res = await api.post(`/onboarding/requests/${requestId}/assign`, form);
      toast.success("Team assigned");
      onSaved && onSaved(res.data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[520px] overflow-y-auto p-0 bg-[#FAFBFC]">
        <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
          <SheetTitle>Assign Home Care Team</SheetTitle>
          <SheetDescription>Assignment is allowed only when both Medical and Operational approvals are complete.</SheetDescription>
        </SheetHeader>
        <div className="p-6">
          <FormSection title="Assignment" testId="ob-assign-form">
            <FormField label="Home Care Team" required>
              <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                <SelectTrigger data-testid="ob-assign-team"><SelectValue placeholder="Select team" /></SelectTrigger>
                <SelectContent>
                  {teams.map((t) => <SelectItem key={t.id} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Team lead">
              <Select value={form.team_lead_id} onValueChange={(v) => setForm({ ...form, team_lead_id: v })}>
                <SelectTrigger data-testid="ob-assign-lead"><SelectValue placeholder="Select team lead" /></SelectTrigger>
                <SelectContent>
                  {leads.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Visit date" required>
              <Input type="date" data-testid="ob-assign-date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} />
            </FormField>
            <FormField label="Visit time">
              <Input type="time" data-testid="ob-assign-time" value={form.visit_time} onChange={(e) => setForm({ ...form, visit_time: e.target.value })} />
            </FormField>
            <FormField label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="ob-assign-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {priorities.length > 0 ? priorities.map((p) => (
                    <SelectItem key={p.id} value={p.value}>{p.label}</SelectItem>
                  )) : (
                    <>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="stat">STAT</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Notes" full>
              <Textarea rows={3} data-testid="ob-assign-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </FormField>
          </FormSection>
        </div>
        <StickyActionBar>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-assign-save">
            {saving ? "Assigning…" : "Confirm assignment"}
          </Button>
        </StickyActionBar>
      </SheetContent>
    </Sheet>
  );
}


// --------------- Visit Checklist Sheet ---------------
const CHECKLIST_ITEMS = [
  ["patient_verification", "Patient verification"],
  ["address_verification", "Address verification"],
  ["home_env_verification", "Home environment verification"],
  ["connectivity_verification", "Connectivity verification"],
  ["caregiver_verification", "Caregiver verification"],
  ["rpm_readiness", "RPM readiness verification"],
  ["infection_control", "Infection control assessment"],
  ["waste_management", "Waste management assessment"],
];

function VisitChecklistSheet({ open, onOpenChange, requestId, initial, canComplete, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial || {});
  }, [open, initial]);

  async function save(complete = false) {
    setSaving(true);
    try {
      const res = await api.post(`/onboarding/requests/${requestId}/save-checklist`, form);
      let result = res.data;
      if (complete) {
        const res2 = await api.post(`/onboarding/requests/${requestId}/complete-visit`);
        result = res2.data;
        toast.success("Visit completed");
      } else {
        toast.success("Checklist saved");
      }
      onSaved && onSaved(result);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[600px] overflow-y-auto p-0 bg-[#FAFBFC]">
        <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
          <SheetTitle>Home Visit Checklist</SheetTitle>
          <SheetDescription>Complete the checklist while on-site. Save progress at any time.</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-4">
          <FormSection title="Verification" testId="ob-visit-verification">
            {CHECKLIST_ITEMS.map(([k, label]) => (
              <FormField key={k} label={label} full>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form[k] === true}
                      onCheckedChange={() => setForm({ ...form, [k]: form[k] === true ? null : true })}
                      data-testid={`ob-visit-${k}-yes`}
                    />
                    Verified
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form[k] === false}
                      onCheckedChange={() => setForm({ ...form, [k]: form[k] === false ? null : false })}
                      data-testid={`ob-visit-${k}-no`}
                    />
                    Not verified
                  </label>
                </div>
              </FormField>
            ))}
          </FormSection>
          <FormSection title="Notes & evidence" testId="ob-visit-notes">
            <FormField label="Additional findings" full>
              <Textarea rows={3} value={form.additional_findings || ""} onChange={(e) => setForm({ ...form, additional_findings: e.target.value })} data-testid="ob-visit-findings" />
            </FormField>
            <FormField label="Visit notes" full>
              <Textarea rows={3} value={form.visit_notes || ""} onChange={(e) => setForm({ ...form, visit_notes: e.target.value })} data-testid="ob-visit-notes-input" />
            </FormField>
          </FormSection>
          <div className="text-xs text-[#6B7280]">Upload photos and other evidence from the request detail "Attachments" tab.</div>
        </div>
        <StickyActionBar>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]">Cancel</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" data-testid="ob-visit-save">
            Save progress
          </Button>
          {canComplete && (
            <Button onClick={() => save(true)} disabled={saving} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-visit-complete">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete visit
            </Button>
          )}
        </StickyActionBar>
      </SheetContent>
    </Sheet>
  );
}
